import { isAdvisoryAccessDenied } from '@/features/advisory-ai/access'
import { useCallback, useEffect, useRef } from 'react'
import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  analyzeAdvisoryReport,
  batchUpdateFindings,
  createAdvisoryAgendaTemplate,
  createAdvisoryReport,
  createConsolidatedReport,
  deleteAdvisoryCall,
  deleteAdvisoryAgendaTemplate,
  fetchConsolidatedAnalyses,
  type ConsolidatedAnalysisRow,
  cloneVoice,
  fetchVoiceClone,
  fetchUserMedia,
  isVoiceCloneProcessing,
  downloadAdvisoryAiReport,
  downloadAdvisoryExportsZip,
  markAdvisoryExportsDownloaded,
  exportAdvisoryAiReport,
  fetchAdvisoryAgendaTemplates,
  fetchAdvisoryAiOrgDetails,
  fetchAdvisoryAiOrgInternalMemory,
  fetchAdvisoryInternalMemorySuggestion,
  fetchAdvisoryAiOrgReports,
  fetchAdvisoryAiOrgs,
  fetchAdvisoryAiReport,
  fetchAdvisoryAiThemeColors,
  fetchAdvisoryDashboardCards,
  fetchAdvisoryExports,
  fetchAdvisoryMappings,
  fetchSurveyForms,
  generateAdvisoryAiReport,
  sendDeepDiveMessage,
  updateAdvisoryAgendaTemplate,
  updateAdvisoryAiOrgProfile,
  updateAdvisoryAiOrgInternalMemory,
  updateAdvisoryAiThemeColors,
  updateAdvisoryCall,
} from './api'
import type { AdvisoryVoiceClone, CloneVoiceInput, UserMedia } from './api'
import {
  fetchPopups,
  fetchPopupSettings,
  hidePopup,
  markAllPopupsRead,
  markPopupRead,
  markPopupsSeen,
  updatePopupSetting,
  type PopupsResponse,
  type UpdatePopupSettingInput,
} from '@/api/advisoryPopups'
import type { PopupSetting } from '@/features/advisory-ai/assistant/types'
import {
  fetchFeedbackStatus,
  submitFeedback,
  type SubmitFeedbackInput,
} from '@/api/advisoryFeedback'
import {
  answerTicket,
  changeTicketStatus,
  fetchAllTickets,
  fetchMyTickets,
  fetchTicketHistory,
  fetchTicketReplies,
  fetchTicketThread,
  raiseTicket,
  sendTicketReply,
  type AllTicketsQuery,
  type RaiseTicketInput,
  type ReplyStatus,
} from '@/api/advisorySupport'
import type { TicketReply, TicketStatus, TicketStatusValue } from '@/features/advisory-ai/assistant/types'
import { fetchHelpArticles } from '@/api/advisoryHelp'
import { queryKeys } from '@/api/queryKeys'
import type {
  AdvisoryAiExportFormat,
  AdvisoryAiOrgFilters,
  AdvisoryAiReportDetail,
  AdvisoryFinding,
  AnalyzeAdvisoryReportInput,
  BatchUpdateFindingsInput,
  CreateAdvisoryAgendaTemplateInput,
  CreateAdvisoryReportInput,
  DeepDiveChatRequest,
  GenerateAdvisoryAiReportInput,
  UpdateAdvisoryAgendaTemplateInput,
  UpdateAdvisoryAiOrgProfileInput,
  UpdateAdvisoryAiOrgInternalMemoryInput,
  UpdateAdvisoryAiThemeColorsInput,
  UpdateAdvisoryCallInput,
} from './types'
import { reportStatusRow } from './consolidated/utils'
import { getCallStatus, getStepProgress, type CallStatus } from './calls/utils'

export function useAdvisoryDashboardCards() {
  return useQuery({
    queryKey: queryKeys.advisory.dashboards(),
    queryFn: fetchAdvisoryDashboardCards,
  })
}

export function useAdvisoryMappings(filters: { name?: string; status?: string } = {}) {
  return useQuery({
    queryKey: queryKeys.advisory.mapping(filters),
    queryFn: () => fetchAdvisoryMappings(filters),
  })
}

export function useSurveyForms(filters: { name?: string; status?: string } = {}) {
  return useQuery({
    queryKey: queryKeys.advisory.surveyForms(filters),
    queryFn: () => fetchSurveyForms(filters),
  })
}

export function useAdvisoryAiOrgs(filters: AdvisoryAiOrgFilters) {
  return useQuery({
    queryKey: queryKeys.advisory.aiOrgs(filters),
    queryFn: () => fetchAdvisoryAiOrgs(filters),
    enabled: !!filters.clientId,
  })
}

export function useAdvisoryAiOrgDetails(orgId: string, clientId: string) {
  return useQuery({
    queryKey: queryKeys.advisory.aiOrgDetail(orgId, clientId),
    queryFn: () => fetchAdvisoryAiOrgDetails(orgId, clientId),
    enabled: !!orgId && !!clientId,
  })
}

export function useAdvisoryAiOrgInternalMemory(orgId: string, clientId: string) {
  return useQuery({
    queryKey: queryKeys.advisory.aiOrgInternalMemory(orgId, clientId),
    queryFn: () => fetchAdvisoryAiOrgInternalMemory(orgId, clientId),
    enabled: !!orgId && !!clientId,
  })
}

export function useCreateAdvisoryReportMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateAdvisoryReportInput) => createAdvisoryReport(input),
    // Fires `first_meeting_created` server-side.
    onSuccess: (_data, input) => invalidateAdvisoryPopups(queryClient, input.clientId),
  })
}

export function useGenerateAdvisoryAiReportMutation() {
  return useMutation({
    mutationFn: (input: GenerateAdvisoryAiReportInput) => generateAdvisoryAiReport(input),
  })
}

export function useUpdateAdvisoryAiOrgProfileMutation(orgId: string, clientId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: UpdateAdvisoryAiOrgProfileInput) => updateAdvisoryAiOrgProfile(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.aiOrgDetail(orgId, clientId) })
    },
  })
}

export function useUpdateAdvisoryAiOrgInternalMemoryMutation(orgId: string, clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateAdvisoryAiOrgInternalMemoryInput) => updateAdvisoryAiOrgInternalMemory(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.aiOrgInternalMemory(orgId, clientId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.aiOrgDetail(orgId, clientId) })
    },
  })
}

export function useAdvisoryAiReport(reportId: string, clientId: string, shouldPoll = false) {
  return useQuery({
    queryKey: queryKeys.advisory.aiReport(reportId, clientId),
    queryFn: () => fetchAdvisoryAiReport(reportId, clientId),
    enabled: !!reportId && !!clientId,
    refetchInterval: (query) => {
      if (!shouldPoll) return false
      // A denial leaves no data, so the status below would read '' - which is not terminal,
      // and the poll would never stop.
      if (isAdvisoryAccessDenied(query.state.error)) return false
      const status = String(query.state.data?.status || '').toUpperCase()
      const terminal =
        status === 'COMPLETED_BRIEF' ||
        status === 'COMPLETED_EXPORT' ||
        status === 'ERROR' ||
        status === 'CHAT_ERROR'
      return terminal ? false : 3000
    },
  })
}


export function useExportAdvisoryAiReportMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ reportId, clientId, exportFormat, voiceCloneId }: {
      reportId: string
      clientId: string
      exportFormat: AdvisoryAiExportFormat
      voiceCloneId?: string | null
    }) => exportAdvisoryAiReport(reportId, clientId, exportFormat, voiceCloneId),
    onSuccess: (_, { reportId, clientId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.aiExports(reportId, clientId) })
    },
  })
}

export function useDownloadAdvisoryExportsZipMutation() {
  return useMutation({
    mutationFn: ({
      reportId,
      clientId,
      exportTypes,
    }: {
      reportId: string
      clientId: string
      exportTypes: AdvisoryAiExportFormat[]
    }) => downloadAdvisoryExportsZip(reportId, clientId, exportTypes),
  })
}

/**
 * Reports a download the user has already taken.
 *
 * Fire-and-forget by design: the file is on disk before this runs, so a failure
 * costs an "Exported" pop-up and nothing else — surfacing it would tell the user
 * their download failed when it did not.
 *
 * The 204 deliberately says nothing about which pop-ups it wrote, so the feed is
 * read back on success rather than waiting up to 180s for the poll.
 */
export function useMarkExportsDownloadedMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      reportId,
      clientId,
      exportIds,
    }: {
      reportId: string
      clientId: string
      exportIds: string[]
    }) => markAdvisoryExportsDownloaded(reportId, clientId, exportIds),
    onSuccess: (sent, { clientId }) => {
      if (sent) void refetchAdvisoryPopups(queryClient, clientId)
    },
    // Silent for the user, not for us — otherwise the only trace of a download
    // that never got announced is a pop-up that never arrives.
    onError: (error) => console.warn('[advisory-exports] could not report download', error),
  })
}

export function useAdvisoryAiOrgReports(orgId: string, clientId: string, pageIndex: number, pageSize: number) {
  return useQuery({
    queryKey: queryKeys.advisory.aiOrgReports(orgId, clientId, pageIndex, pageSize),
    queryFn: () => fetchAdvisoryAiOrgReports(orgId, pageIndex, pageSize, clientId),
    enabled: !!orgId && !!clientId,
  })
}

export function useDeleteAdvisoryCallMutation(orgId: string, clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reportId: string) => deleteAdvisoryCall(reportId, clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advisory', 'aiOrgReports', orgId, clientId] })
    },
  })
}
export function useDownloadAdvisoryAiReportMutation() {
  return useMutation({
    mutationFn: ({ reportId, clientId, exportType }: { reportId: string; clientId: string; exportType: string }) =>
      downloadAdvisoryAiReport(reportId, clientId, exportType),
  })
}

export function useAdvisoryAgendaTemplates(clientId: string) {
  return useQuery({
    queryKey: queryKeys.advisory.aiAgendaTemplates(clientId),
    queryFn: () => fetchAdvisoryAgendaTemplates(clientId),
    enabled: !!clientId,
  })
}

export function useCreateAdvisoryAgendaTemplateMutation(clientId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateAdvisoryAgendaTemplateInput) => createAdvisoryAgendaTemplate(input),
    // Awaited so the unsaved default is gone from the list before Save re-enables; otherwise a second save duplicates it.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.advisory.aiAgendaTemplates(clientId) })
    },
  })
}

export function useUpdateAdvisoryAgendaTemplateMutation(clientId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: UpdateAdvisoryAgendaTemplateInput) => updateAdvisoryAgendaTemplate(input),
    // Awaited for the same reason as create: a rename to the default's name must hide the default before Save re-enables.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.advisory.aiAgendaTemplates(clientId) })
    },
  })
}

export function useDeleteAdvisoryAgendaTemplateMutation(clientId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (templateId: string) => deleteAdvisoryAgendaTemplate(templateId, clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.aiAgendaTemplates(clientId) })
    },
  })
}

export function useAdvisoryAiThemeColors(clientId: string) {
  return useQuery({
    queryKey: queryKeys.advisory.aiThemeColors(clientId),
    queryFn: () => fetchAdvisoryAiThemeColors(clientId),
    enabled: !!clientId,
  })
}

export function useUpdateAdvisoryAiThemeColorsMutation(clientId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: Omit<UpdateAdvisoryAiThemeColorsInput, 'clientId'>) =>
      updateAdvisoryAiThemeColors({ ...input, clientId }),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.advisory.aiThemeColors(clientId), data)
      // Fires `branding_updated` server-side.
      invalidateAdvisoryPopups(queryClient, clientId)
    },
  })
}

export function useAnalyzeAdvisoryReportMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: AnalyzeAdvisoryReportInput) => analyzeAdvisoryReport(input),
    // Re-running the analysis rewrites what every export is built from, so any
    // finished export is outdated from here on. Only on success: a failed run
    // changes nothing, and re-reading the list would be a wasted round trip.
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.advisory.aiExports(input.reportId, input.clientId),
      })
    },
  })
}

type FindingsBatchInput = Omit<BatchUpdateFindingsInput, 'reportId' | 'clientId'>

function applyFindingsDiff(
  report: AdvisoryAiReportDetail,
  reportId: string,
  input: FindingsBatchInput
): AdvisoryAiReportDetail {
  const deleted = new Set(input.deleted)
  const updatedById = new Map(input.updated.map((u) => [u.advisoryFindingId, u]))

  const kept = (report.findings ?? [])
    .filter((f) => !deleted.has(f.advisoryFindingId))
    .map((f) => {
      const u = updatedById.get(f.advisoryFindingId)
      if (!u) return f
      return { ...f, type: u.type, heading: u.heading ?? null, description: u.description, hidden: u.hidden ?? false }
    })

  const created: AdvisoryFinding[] = input.created.map((c, i) => ({
    advisoryFindingId: `optimistic-${i}-${Math.random().toString(36).slice(2)}`,
    advisoryCallId: reportId,
    advisoryAgendaId: input.advisoryAgendaId,
    type: c.type,
    heading: c.heading ?? null,
    description: c.description,
    createdAt: new Date().toISOString(),
    hidden: c.hidden ?? false,
  }))

  return { ...report, findings: [...kept, ...created] }
}

export function useBatchUpdateFindingsMutation(reportId: string, clientId: string) {
  const queryClient = useQueryClient()
  const reportKey = queryKeys.advisory.aiReport(reportId, clientId)
  return useMutation({
    mutationFn: (input: FindingsBatchInput) => batchUpdateFindings({ ...input, reportId, clientId }),
    // Optimistically apply the edit to the cached report so the change shows immediately.
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: reportKey })
      const previous = queryClient.getQueryData<AdvisoryAiReportDetail>(reportKey)
      if (previous) {
        queryClient.setQueryData<AdvisoryAiReportDetail>(reportKey, applyFindingsDiff(previous, reportId, input))
      }
      return { previous }
    },
    // On failure, roll back to the pre-edit snapshot.
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(reportKey, context.previous)
    },
    // Reconcile with the server once the request settles.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: reportKey })
      // The server flags finished exports as outdated when the analysis changes,
      // and isOutdated is what surfaces Regenerate. Without this the cached list
      // keeps saying they are current until the page is reloaded.
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.aiExports(reportId, clientId) })
      // Fires `first_recommendation_edited` server-side.
      invalidateAdvisoryPopups(queryClient, clientId)
    },
  })
}

export function useUpdateAdvisoryCallMutation(reportId: string, clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Omit<UpdateAdvisoryCallInput, 'reportId' | 'clientId'>) =>
      updateAdvisoryCall({ ...input, reportId, clientId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.aiReport(reportId, clientId) })
      // Summary and advisory notes feed the exports too, so a change here can
      // outdate them the same way a findings edit does.
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.aiExports(reportId, clientId) })
      // The meeting lists sort and label by meeting date, so a reschedule must show there at once.
      queryClient.invalidateQueries({ queryKey: ['advisory', 'aiOrgReports'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.consolidatedList(clientId) })
      // The general update call carries agenda and notes, so this is the source
      // of `first_agenda_created`, `notes_added` and `first_notes_added`.
      invalidateAdvisoryPopups(queryClient, clientId)
    },
  })
}

// The current user's combined voice + avatar detail (presigned URLs). One call backs the
// settings tab, the profile section, and the export screen's voice picker + video gate.
export function useUserMedia() {
  return useQuery({
    queryKey: queryKeys.advisory.media(),
    queryFn: fetchUserMedia,
  })
}

// Polls one clone's status (used while it is processing); stops once it reaches a terminal state.
export function useVoiceClone(advisoryVoiceCloneId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.advisory.voiceClone(advisoryVoiceCloneId),
    queryFn: () => fetchVoiceClone(advisoryVoiceCloneId),
    enabled: enabled && !!advisoryVoiceCloneId,
    refetchInterval: (query) => {
      const data = query.state.data as AdvisoryVoiceClone | null | undefined
      return data && isVoiceCloneProcessing(data.status) ? 5000 : false
    },
  })
}

export function useCloneVoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CloneVoiceInput) => cloneVoice(input),
    onSuccess: (result) => {
      // Optimistically show the new (Pending) clone in the combined media cache, then refetch.
      queryClient.setQueryData<UserMedia>(queryKeys.advisory.media(), (old) => ({
        avatar: old?.avatar ?? { hasAvatar: false, url: null },
        voice: {
          advisoryVoiceCloneId: result.advisoryVoiceCloneId,
          voiceName: result.voiceName,
          isMale: result.isMale,
          status: result.status,
          hasSample: old?.voice?.hasSample ?? false,
          sampleUrl: old?.voice?.sampleUrl ?? '',
          errorMessage: '',
          createdAt: old?.voice?.createdAt ?? new Date().toISOString(),
        },
      }))
      void queryClient.invalidateQueries({ queryKey: queryKeys.advisory.media() })
    },
  })
}

export function useAdvisoryInternalMemorySuggestionMutation(clientId: string, orgId: string) {
  return useMutation({
    mutationFn: (meetingDate: string) =>
      fetchAdvisoryInternalMemorySuggestion(clientId, orgId, meetingDate),
  })
}

export function useAdvisoryExports(
  reportId: string,
  clientId: string,
  enabled = true,
  shouldPoll = false,
  forcePoll = false
) {
  return useQuery({
    queryKey: queryKeys.advisory.aiExports(reportId, clientId),
    queryFn: () => fetchAdvisoryExports(reportId, clientId),
    enabled: !!reportId && !!clientId && enabled,
    // Deliberately keeps the 60s client default. Every mutation that can change
    // what an export is built from invalidates this key, and that invalidation is
    // the signal the step reads to decide whether it is re-checking. Dropping the
    // stale time to 0 would refetch on every visit and make that signal meaningless.
    refetchInterval: (query) => {
      if (!shouldPoll) return false
      if (forcePoll) return 3000
      const data = query.state.data
      if (!data || data.length === 0) return 3000
      const hasInProgress = data.some((exp) => {
        const s = String(exp.status || '').toUpperCase()
        return s === 'PENDING_EXPORT' || s === 'EXPORTING'
      })
      return hasInProgress ? 3000 : false
    },
  })
}

export function useDeepDiveChatMutation(
  callId: string,
  clientId: string,
  options?: { onFindingsUpdated?: () => void }
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Omit<DeepDiveChatRequest, 'advisoryCallId' | 'clientId'>) =>
      sendDeepDiveMessage({ ...input, advisoryCallId: callId, clientId }),
    onSuccess: (data) => {
      const queued = 'status' in data && data.status === 'queued'
      const message = 'message' in data ? data.message : undefined
      const isUpdate = message?.intent === 'update'
      const hasUpdates = (message?.updatedEntities?.length ?? 0) > 0

      if (queued || isUpdate || hasUpdates) {
        queryClient.invalidateQueries({ queryKey: queryKeys.advisory.aiReport(callId, clientId) })
        // Preppy edited the findings, which outdates the exports exactly as a
        // hand edit does.
        queryClient.invalidateQueries({ queryKey: queryKeys.advisory.aiExports(callId, clientId) })
        options?.onFindingsUpdated?.()
      }
    },
  })
}

// ---------------------------------------------------------------------------
// Consolidated Analysis (one advisoryCall spanning multiple orgIds)
// ---------------------------------------------------------------------------

export type ConsolidatedOrg = {
  orgId: string
  orgName: string
}

export type ConsolidatedAnalysis = {
  advisoryCallId: string
  clientId: string
  orgs: ConsolidatedOrg[]
  createdAt: string
}

export type EnrichedConsolidatedAnalysis = {
  group: ConsolidatedAnalysis
  // ConsolidatedAnalysisRow rather than the bare detail: the card needs canInteract off it.
  report?: ConsolidatedAnalysisRow
  status: CallStatus
  step: { current: number; total: number; label: string }
}

/**
 * List page: each consolidated analysis is one advisoryCall spanning >= 2 orgs.
 * The backend list endpoint returns the full report plus member orgIds; org names
 * are resolved from the client's org list (the report payload doesn't carry them).
 */
/**
 * The raw consolidated list, without the org-name enrichment.
 *
 * `useConsolidatedAnalyses` below also fetches every organisation in order to
 * label them, which is a second request the assistant does not need — it only
 * wants an id to point a walkthrough at. Same query key as the list page, so
 * this is a cache read wherever that page has already loaded.
 */
export function useConsolidatedAnalysisList(clientId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.advisory.consolidatedList(clientId),
    queryFn: () => fetchConsolidatedAnalyses(clientId),
    enabled: !!clientId && enabled,
  })
}

export function useConsolidatedAnalyses(clientId: string) {
  const listQuery = useQuery({
    queryKey: queryKeys.advisory.consolidatedList(clientId),
    queryFn: () => fetchConsolidatedAnalyses(clientId),
    enabled: !!clientId,
  })

  const orgsQuery = useAdvisoryAiOrgs({ clientId })

  const nameByOrgId: Record<string, string> = {}
  for (const org of orgsQuery.data ?? []) {
    nameByOrgId[org.companyId] = org.companyName || org.name
  }

  const enriched: EnrichedConsolidatedAnalysis[] = (listQuery.data ?? []).map((row) => {
    const orgs: ConsolidatedOrg[] = row.orgIds.map((orgId) => ({
      orgId,
      orgName: nameByOrgId[orgId] || orgId,
    }))
    const group: ConsolidatedAnalysis = {
      advisoryCallId: row.advisoryCallId,
      clientId,
      orgs,
      createdAt: row.createdAt ?? '',
    }
    const statusRow = reportStatusRow(row)
    return { group, report: row, status: getCallStatus(statusRow), step: getStepProgress(statusRow) }
  })

  return {
    groups: enriched,
    isLoading: listQuery.isLoading || orgsQuery.isLoading,
    isFetching: listQuery.isFetching || orgsQuery.isFetching,
    error: listQuery.error,
    refetch: () => {
      void listQuery.refetch()
      void orgsQuery.refetch()
    },
  }
}

/** Wizard: the group record (title + member orgs) for one consolidated advisoryCall. */
export function useConsolidatedAnalysis(advisoryCallId: string, clientId: string) {
  const { groups, isLoading } = useConsolidatedAnalyses(clientId)
  const group = groups.find((g) => g.group.advisoryCallId === advisoryCallId)?.group ?? null
  return { group, isLoading }
}

export function useCreateConsolidatedAnalysisMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      clientId: string
      title: string
      companies: ConsolidatedOrg[]
    }): Promise<ConsolidatedAnalysis> => {
      const { advisoryCallId } = await createConsolidatedReport({
        orgIds: input.companies.map((c) => c.orgId),
        clientId: input.clientId,
        title: input.title,
      })
      return {
        advisoryCallId,
        clientId: input.clientId,
        orgs: input.companies,
        createdAt: '',
      }
    },
    onSuccess: (group) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.consolidatedList(group.clientId) })
    },
  })
}

export function useDeleteConsolidatedAnalysisMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (group: ConsolidatedAnalysis) => {
      await deleteAdvisoryCall(group.advisoryCallId, clientId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.consolidatedList(clientId) })
    },
  })
}

// ---------------------------------------------------------------------------
// Preppy pop-ups — see PREPPY-POPUPS-SPEC.md
// ---------------------------------------------------------------------------

/**
 * Poll interval for the pop-up feed.
 *
 * Deliberately not 60s. The API is a single Lambda behind API Gateway, so every
 * poll is one invocation per user per interval — a firm of twenty with the tab
 * parked open would be ~28,800 invocations a day for a feed that changes a few
 * times a week. Three minutes late is invisible for a corner bubble.
 */
const POPUPS_POLL_MS = 180_000

/**
 * Refetch the pop-up feed now, rather than waiting up to 180s for the poll.
 *
 * The poll is deliberately slow — one Lambda invocation per user per interval —
 * which is right for pop-ups about other people's actions and about daily
 * sweeps, where nobody is waiting. It is wrong for confirmations of something
 * you just did: "Saved" arriving two minutes after you saved reads as a glitch.
 *
 * So every mutation that causes a server-side pop-up event calls this, and the
 * interval is left governing only the messages nobody is watching for.
 */
export function invalidateAdvisoryPopups(queryClient: QueryClient, clientId?: string) {
  // Falls back to a prefix match when the caller has no clientId to hand. Only
  // one client is ever active, so it is equivalent — and it saves threading an
  // id through components that have no other use for it.
  void queryClient.invalidateQueries({
    queryKey: clientId ? queryKeys.advisory.popups(clientId) : ['advisory', 'popups'],
  })
}

const popupsQueryOptions = (clientId: string) =>
  queryOptions({
    queryKey: queryKeys.advisory.popups(clientId),
    queryFn: () => fetchPopups(clientId),
  })

/**
 * Read the feed back now, and wait for it.
 *
 * Stronger than invalidating, which refetches only where an observer happens to
 * be mounted. That is true wherever this is called from today, but the caller is
 * reacting to a 204 that says nothing about what it wrote, so the read is the
 * only way to learn — worth issuing outright rather than leaving to a condition
 * elsewhere in the tree. Never rejects: a failed read must not fail the action
 * that wrote the pop-up.
 *
 * Note `fetchQuery` ignores `enabled`, so this fires the GET wherever it is
 * called — and that GET records the AdvicePrep visit (see below). Keep callers
 * on AdvicePrep routes.
 */
export function refetchAdvisoryPopups(queryClient: QueryClient, clientId: string) {
  return queryClient
    .fetchQuery({ ...popupsQueryOptions(clientId), staleTime: 0 })
    .catch(() => undefined)
}

/**
 * The feed, and the AdvicePrep visit signal.
 *
 * This query is load-bearing beyond its data: the GET records the visit that the
 * two `inactivity` events are computed from, and it applies burst control. So it
 * must only run on AdvicePrep routes — the host mounts it there and nowhere
 * else. Moving it would quietly change when users are told they have been away.
 */
export function useAdvisoryPopups(clientId: string, enabled = true) {
  return useQuery({
    ...popupsQueryOptions(clientId),
    enabled: !!clientId && enabled,
    refetchInterval: POPUPS_POLL_MS,
    // Keeps polling on a hidden tab. A pop-up waiting behind a backgrounded tab
    // is the case the feed exists for, so the visit this GET records now means
    // "the app is open", which is also what the inactivity events ask about.
    refetchIntervalInBackground: true,
  })
}

export function useAdvisoryPopupSettings(clientId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.advisory.popupSettings(clientId),
    queryFn: () => fetchPopupSettings(clientId),
    enabled: !!clientId && enabled,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Read receipts, applied optimistically.
 *
 * The badge has to drop the moment you act, not three minutes later when the
 * poll comes round. The server is deliberately forgiving about unknown or
 * already-read ids, so a rollback on error is the only failure path worth
 * handling.
 */
export function useMarkPopupReadMutation(clientId: string) {
  const queryClient = useQueryClient()
  const key = queryKeys.advisory.popups(clientId)

  return useMutation({
    mutationFn: (popupId: string) => markPopupRead(popupId, clientId),
    onMutate: async (popupId: string) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<PopupsResponse>(key)
      if (previous) {
        queryClient.setQueryData<PopupsResponse>(key, {
          popups: previous.popups.map((p) => (p.id === popupId ? { ...p, unread: false } : p)),
        })
      }
      return { previous }
    },
    onError: (_error, _popupId, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

export function useHidePopupMutation(clientId: string) {
  const queryClient = useQueryClient()
  const key = queryKeys.advisory.popups(clientId)

  return useMutation({
    mutationFn: (popupId: string) => hidePopup(popupId, clientId),
    onMutate: async (popupId: string) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<PopupsResponse>(key)
      if (previous) {
        queryClient.setQueryData<PopupsResponse>(key, {
          popups: previous.popups.filter((p) => p.id !== popupId),
        })
      }
      return { previous }
    },
    onError: (_error, _popupId, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

export function useMarkAllPopupsReadMutation(clientId: string) {
  const queryClient = useQueryClient()
  const key = queryKeys.advisory.popups(clientId)

  return useMutation({
    mutationFn: () => markAllPopupsRead(clientId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<PopupsResponse>(key)
      if (previous) {
        queryClient.setQueryData<PopupsResponse>(key, {
          popups: previous.popups.map((p) => ({ ...p, unread: false })),
        })
      }
      return { previous }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

/**
 * Tells the server a bubble was actually shown.
 *
 * Fire-and-forget on purpose: if it fails the message simply pops again later,
 * which is a far better failure than blocking the queue on a receipt.
 */
export function useMarkPopupsSeenMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (popupIds: string[]) => markPopupsSeen(popupIds, clientId),
    // Only the ids this receipt was for — marking everything seen would swallow
    // a pop-up that arrived while the receipt was in flight.
    onSuccess: (_data, popupIds) => {
      queryClient.setQueryData<PopupsResponse>(queryKeys.advisory.popups(clientId), (prev) =>
        prev
          ? {
              ...prev,
              popups: prev.popups.map((p) => (popupIds.includes(p.id) ? { ...p, seen: true } : p)),
            }
          : prev
      )
    },
  })
}

export function useUpdatePopupSettingMutation(clientId: string) {
  const queryClient = useQueryClient()
  const key = queryKeys.advisory.popupSettings(clientId)

  return useMutation({
    mutationFn: (input: Omit<UpdatePopupSettingInput, 'clientId'>) =>
      updatePopupSetting({ ...input, clientId }),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<PopupSetting[]>(key)
      if (previous) {
        queryClient.setQueryData<PopupSetting[]>(
          key,
          previous.map((s) => (s.key === input.trigger ? { ...s, on: input.enabled } : s))
        )
      }
      return { previous }
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key })
      // A stage switched back on writes no rows retroactively, but one switched
      // off should stop showing what is already queued.
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.popups(clientId) })
    },
  })
}

/**
 * Refetches the pop-up feed after something that has already written to it,
 * instead of waiting out the three-minute poll.
 *
 * No delay: the caller acts on a status the server has already returned, so the
 * row it implies exists by then.
 */
export function useRefreshPopups(clientId: string) {
  const queryClient = useQueryClient()
  return useCallback(() => invalidateAdvisoryPopups(queryClient, clientId), [clientId, queryClient])
}

// ---------------------------------------------------------------------------
// Preppy feedback — see PREPPY-FEEDBACK-SPEC.md
// ---------------------------------------------------------------------------

/**
 * Whether this user has ever rated, and what they last said.
 *
 * Read straight from the feedback table server-side, so there is no flag to keep
 * in sync. Deliberately not firm-scoped: "have we already asked you?" is a
 * question about the person, so switching firms does not reset it. `clientId`
 * still goes on the request because every route in this domain takes it, and it
 * is what the permission check runs against.
 */
export function useAdvisoryFeedbackStatus(clientId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.advisory.feedbackStatus(clientId),
    queryFn: () => fetchFeedbackStatus(clientId),
    enabled: !!clientId && enabled,
    // Changes only when this user submits, and that invalidates it directly.
    staleTime: 10 * 60 * 1000,
  })
}

/**
 * Submit feedback.
 *
 * No optimistic update on purpose. Everywhere else in the assistant an
 * optimistic write is right because failure is invisible and cheap; here the
 * form is the only copy of something a person took the trouble to write, so it
 * must survive a failed request. The caller clears it on success and not before.
 */
export function useSubmitFeedbackMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Omit<SubmitFeedbackInput, 'clientId'>) =>
      submitFeedback({ ...input, clientId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.feedbackStatus(clientId) })
    },
  })
}

// ---------------------------------------------------------------------------
// Preppy support — see PREPPY-SUPPORT-SPEC.md
// ---------------------------------------------------------------------------

/**
 * The caller's own tickets.
 *
 * Polled so the dock badge and the corner bubble notice a reply while the user
 * is already in the app, panel closed. Same cadence as the pop-up feed, and not
 * on a hidden tab: nobody is there to see the bubble.
 */
export function useAdvisorySupportTickets(clientId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.advisory.supportTickets(clientId),
    queryFn: () => fetchMyTickets(clientId),
    enabled: !!clientId && enabled,
    staleTime: 60 * 1000,
    refetchInterval: POPUPS_POLL_MS,
    refetchOnWindowFocus: true,
  })
}

/**
 * Raise a ticket. No optimistic update — the draft is the only copy of what the
 * user wrote, so the caller clears it on success and not before.
 */
export function useRaiseTicketMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Omit<RaiseTicketInput, 'clientId'>) => raiseTicket({ ...input, clientId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.supportTickets(clientId) })
    },
  })
}

/**
 * The raiser's view of one ticket's thread. It has no poller of its own:
 * `version` is the ticket's `updatedAt` from the polled list, and a change to
 * it means a new message, so the thread is re-read then.
 */
export function useCustomerTicketThread(ticketId: string, clientId: string, enabled: boolean, version: string) {
  const queryClient = useQueryClient()
  const seen = useRef({ ticketId, version })

  useEffect(() => {
    const previous = seen.current
    seen.current = { ticketId, version }
    if (!ticketId || previous.ticketId !== ticketId || !previous.version || previous.version === version) return
    queryClient.invalidateQueries({ queryKey: queryKeys.advisory.supportTicketThread(ticketId, clientId) })
  }, [clientId, queryClient, ticketId, version])

  return useQuery({
    queryKey: queryKeys.advisory.supportTicketThread(ticketId, clientId),
    queryFn: () => fetchTicketThread(ticketId, clientId),
    enabled: !!ticketId && !!clientId && enabled,
    refetchOnWindowFocus: true,
  })
}

/**
 * The raiser answering support. Not optimistic: the box keeps the text until
 * the server has it, and the ticket list is refetched because an answer to an
 * `awaiting` ticket moves it to `reviewing` server-side.
 */
export function useAnswerTicketMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, body }: { ticketId: string; body: string }) =>
      answerTicket(ticketId, clientId, body),
    onSuccess: (reply, { ticketId }) => {
      queryClient.setQueryData<TicketReply[]>(
        queryKeys.advisory.supportTicketThread(ticketId, clientId),
        (current) => (current ? [...current, reply] : current)
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.supportTicketThread(ticketId, clientId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.supportTickets(clientId) })
    },
  })
}

/**
 * Every ticket, across every client — the admin console (spec §3.3).
 *
 * `clientId` is not sent and is not a filter; it scopes the query key and gates
 * `enabled`. Narrowing to one client is `clientFilter`. `placeholderData` keeps
 * the previous page on screen so filtering does not blank the table.
 */
export function useAllSupportTickets(
  params: AllTicketsQuery,
  enabled = true
) {
  const { clientId, ...filters } = params
  return useQuery({
    queryKey: queryKeys.advisory.supportTicketsAll(clientId, filters),
    queryFn: () => fetchAllTickets(params),
    enabled: !!clientId && enabled,
    placeholderData: (previous) => previous,
    staleTime: 30 * 1000,
    // Customers change statuses from Preppy, which nothing tells the console about.
    refetchOnWindowFocus: true,
  })
}

/** Everything the console shows, list and open ticket alike, re-read from the API. */
export function useRefreshSupportConsole() {
  const queryClient = useQueryClient()
  return useCallback(
    () =>
      Promise.all(
        ['supportTicketsAll', 'supportTicketHistory', 'supportTicketReplies'].map((key) =>
          queryClient.invalidateQueries({ queryKey: ['advisory', key] })
        )
      ),
    [queryClient]
  )
}

/**
 * Move a ticket between states (spec §3.4). Invalidates every console page
 * rather than patching one row, since the change can move the ticket out of the
 * current status filter entirely.
 */
export function useChangeTicketStatusMutation(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, status }: { ticketId: string; status: TicketStatus }) =>
      changeTicketStatus(ticketId, status),
    // Settled, not success: a refused change can still mean the list was out of date.
    onSettled: (_data, _error, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ['advisory', 'supportTicketsAll'] })
      queryClient.invalidateQueries({
        queryKey: queryKeys.advisory.supportTicketHistory(ticketId, clientId),
      })
      // The raiser may be this admin — rare, but the panel would otherwise
      // disagree with the console they just used.
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.supportTickets(clientId) })
    },
  })
}

/**
 * The append-only status log for one ticket (spec §3.6). Admin only.
 *
 * `clientId` is for the query key, not the request — the endpoint takes only a
 * ticket id. It still guards `enabled`, because there is no session to
 * authorise against before a client is selected.
 */
export function useTicketHistory(ticketId: string, clientId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.advisory.supportTicketHistory(ticketId, clientId),
    queryFn: () => fetchTicketHistory(ticketId),
    enabled: !!ticketId && !!clientId && enabled,
    refetchOnWindowFocus: true,
  })
}

/**
 * The ticket's status on the server right now, read past any cache. For a
 * decision that writes: the list row may be minutes old, and the status
 * endpoint applies whatever it is sent.
 */
export function useFetchLatestTicketStatus(clientId: string) {
  const queryClient = useQueryClient()
  return useCallback(
    async (ticketId: string): Promise<TicketStatusValue | null> => {
      const history = await queryClient.fetchQuery({
        queryKey: queryKeys.advisory.supportTicketHistory(ticketId, clientId),
        queryFn: () => fetchTicketHistory(ticketId),
        staleTime: 0,
      })
      return history[history.length - 1]?.toStatus ?? null
    },
    [clientId, queryClient]
  )
}

export function useTicketReplies(ticketId: string, clientId: string) {
  return useQuery({
    queryKey: queryKeys.advisory.supportTicketReplies(ticketId, clientId),
    queryFn: () => fetchTicketReplies(ticketId),
    enabled: !!ticketId && !!clientId,
    refetchOnWindowFocus: true,
  })
}

type SendTicketReplyHandlers = {
  /**
   * `email.requested` is what the admin asked for, `email.saved` what came back.
   * They differ when the API has not shipped `sentByEmail` yet — the reply saves,
   * the notice email goes out anyway, and only the caller can say so.
   */
  onSent?: (ticketId: string, email: { requested: boolean; saved: boolean }) => void
  onFailed?: (ticketId: string, error: unknown) => void
}

/**
 * Support's reply: posts to the thread and sets the status in the same call.
 * Nothing is optimistic: a failure means nothing was saved.
 *
 * The handlers run at the mutation level, not per `mutate` call: per-call
 * callbacks are dropped if the caller unmounts, and the drawer can close while
 * a reply is in flight — the sent text would then sit in the draft, ready to
 * go out twice.
 */
export function useSendTicketReplyMutation(clientId: string, handlers: SendTicketReplyHandlers = {}) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      ticketId,
      body,
      status,
      sentByEmail,
    }: {
      ticketId: string
      body: string
      status: ReplyStatus
      sentByEmail?: boolean
    }) => sendTicketReply(ticketId, { body, status, sentByEmail }),
    onSuccess: (reply, { ticketId, sentByEmail }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.supportTicketReplies(ticketId, clientId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.supportTicketHistory(ticketId, clientId) })
      queryClient.invalidateQueries({ queryKey: ['advisory', 'supportTicketsAll'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.advisory.supportTickets(clientId) })
      handlers.onSent?.(ticketId, { requested: !!sentByEmail, saved: reply.sentByEmail })
    },
    onError: (error, { ticketId }) => handlers.onFailed?.(ticketId, error),
  })
}

// ---------------------------------------------------------------------------
// Preppy help articles — see PREPPY-HELP-SPEC.md
// ---------------------------------------------------------------------------

/**
 * The Help Centre, proxied from Intercom.
 *
 * The most cacheable data in the product — help copy changes a few times a
 * month, the server caches it for an hour, and it is identical for every user.
 * `retry: 1` so a failure surfaces in a second rather than after three backoffs.
 */
export function useHelpArticles(clientId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.advisory.helpArticles(clientId),
    queryFn: () => fetchHelpArticles(clientId),
    enabled: !!clientId && enabled,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  })
}
